import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Mail, Paperclip, ShieldCheck, Send, Eye, X, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABELS = {
    "": "Tous les types", facture: "Facture", soumission: "Soumission",
    relance: "Relance", "relevé": "Relevé", autre: "Autre",
};
const TRIGGER_LABELS = { "": "Tous", user: "Utilisateur", scheduler: "Planificateur", system: "Système" };
const STATUS_BADGE = {
    sent: "bg-[#e6f4ea] text-[#0b6b2f] border-[#8ec79d]",
    failed: "bg-[#fbe8e8] text-[#8a1f1f] border-[#e8a5a5]",
    "en cours": "bg-[#fdecd8] text-[#9a3b00] border-[#e8b588]",
    delivered: "bg-[#e6f4ea] text-[#0b6b2f] border-[#8ec79d]",
    bounced: "bg-[#fbe8e8] text-[#8a1f1f] border-[#e8a5a5]",
};
const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" });
};

export const EmailJournalPanel = () => {
    const { http } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [f, setF] = useState({ customer: "", document: "", email_type: "", trigger: "", date_from: "", date_to: "" });
    const [detail, setDetail] = useState(null);
    const [settings, setSettings] = useState({ enabled: false, address: "" });
    const [savingSettings, setSavingSettings] = useState(false);
    const [testMsg, setTestMsg] = useState("");

    const load = useCallback(async () => {
        setLoading(true); setError("");
        try {
            const params = new URLSearchParams();
            Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
            const { data } = await http.get(`/admin/email-journal?${params.toString()}`);
            setRows(data.entries || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Erreur de chargement du journal");
        } finally { setLoading(false); }
    }, [http, f]);

    const loadSettings = useCallback(async () => {
        try { const { data } = await http.get("/admin/email-journal/settings"); setSettings(data); } catch (_) { /* noop */ }
    }, [http]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadSettings(); }, [loadSettings]);

    const openDetail = async (mid) => {
        try { const { data } = await http.get(`/admin/email-journal/${mid}`); setDetail(data); }
        catch (e) { setError(e?.response?.data?.detail || "Erreur de chargement du détail"); }
    };

    const viewAttachment = async (mid, sha) => {
        try {
            const res = await http.get(`/admin/email-journal/${mid}/attachment/${sha}`, { responseType: "blob" });
            const integrity = res.headers["x-integrity"];
            if (integrity === "mismatch") alert("⚠️ ALERTE INTÉGRITÉ : l'empreinte de la pièce jointe ne correspond plus (fichier altéré).");
            const url = window.URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (e) { alert("Impossible d'ouvrir la pièce jointe"); }
    };

    const saveSettings = async () => {
        setSavingSettings(true); setTestMsg("");
        try {
            const { data } = await http.post("/admin/email-journal/settings", settings);
            setSettings(data); setTestMsg("Réglage enregistré.");
        } catch (e) { setTestMsg(e?.response?.data?.detail || "Erreur d'enregistrement"); }
        finally { setSavingSettings(false); }
    };

    const sendTest = async () => {
        setTestMsg("");
        try { const { data } = await http.post("/admin/email-journal/test-email"); setTestMsg(`Courriel de test envoyé à ${data.to}.`); load(); }
        catch (e) { setTestMsg(e?.response?.data?.detail || "Échec du test"); }
    };

    return (
        <div data-testid="email-journal-panel">
            <div className="flex items-start md:items-center justify-between mb-6 gap-3 flex-col md:flex-row">
                <div className="hidden md:block">
                    <div className="tech-stamp mb-1">Comptabilité · Traçabilité</div>
                    <h1 className="font-display font-bold uppercase text-3xl tracking-tight flex items-center gap-2">
                        <Mail className="w-7 h-7 text-[#2f4f7f]" /> Journal des courriels
                    </h1>
                </div>
                <button onClick={load} data-testid="email-journal-refresh" className="btn-secondary !py-2 !px-3 !text-xs">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            {/* Réglage BCC */}
            <div className="border border-[#dde5f0] bg-white p-4 mb-5">
                <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="w-4 h-4 text-[#2f4f7f]" />
                    <h2 className="tech-stamp">Copie cachée (BCC) sur tous les envois</h2>
                </div>
                <div className="flex flex-col md:flex-row gap-3 md:items-end">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" data-testid="bcc-enabled" checked={settings.enabled}
                            onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.checked }))} />
                        Activer
                    </label>
                    <input type="email" placeholder="votre@adresse.com" data-testid="bcc-address"
                        value={settings.address || ""} onChange={(e) => setSettings((s) => ({ ...s, address: e.target.value }))}
                        className="px-3 py-2 border border-[#c5d4e7] text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-[#2f4f7f]" />
                    <button onClick={saveSettings} disabled={savingSettings} data-testid="bcc-save" className="btn-primary !py-2 !text-sm">
                        Enregistrer
                    </button>
                    <button onClick={sendTest} data-testid="bcc-test" className="btn-secondary !py-2 !text-sm flex items-center gap-1">
                        <Send className="w-3.5 h-3.5" /> Envoyer un test
                    </button>
                </div>
                {testMsg && <p className="text-xs text-[#2f4f7f] mt-2" data-testid="bcc-msg">{testMsg}</p>}
            </div>

            {/* Filtres */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
                <input placeholder="Client" data-testid="filter-customer" value={f.customer}
                    onChange={(e) => setF({ ...f, customer: e.target.value })} className="px-2 py-2 border border-[#c5d4e7] text-sm" />
                <input placeholder="Document (ex. FAC-0001)" data-testid="filter-document" value={f.document}
                    onChange={(e) => setF({ ...f, document: e.target.value })} className="px-2 py-2 border border-[#c5d4e7] text-sm" />
                <select data-testid="filter-type" value={f.email_type} onChange={(e) => setF({ ...f, email_type: e.target.value })}
                    className="px-2 py-2 border border-[#c5d4e7] text-sm">
                    {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <select data-testid="filter-trigger" value={f.trigger} onChange={(e) => setF({ ...f, trigger: e.target.value })}
                    className="px-2 py-2 border border-[#c5d4e7] text-sm">
                    {Object.entries(TRIGGER_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input type="date" data-testid="filter-date-from" value={f.date_from}
                    onChange={(e) => setF({ ...f, date_from: e.target.value })} className="px-2 py-2 border border-[#c5d4e7] text-sm" />
                <input type="date" data-testid="filter-date-to" value={f.date_to}
                    onChange={(e) => setF({ ...f, date_to: e.target.value })} className="px-2 py-2 border border-[#c5d4e7] text-sm" />
            </div>

            {error && <div className="mb-4 p-4 border border-red-400 bg-red-50 text-red-800 text-sm">{error}</div>}

            <div className="border border-[#dde5f0] bg-white">
                <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#dde5f0] bg-[#f3f6fb] tech-stamp">
                    <div className="col-span-2">Date</div>
                    <div className="col-span-3">Destinataire</div>
                    <div className="col-span-3">Objet</div>
                    <div className="col-span-1">Type</div>
                    <div className="col-span-1">Décl.</div>
                    <div className="col-span-2">Statut</div>
                </div>
                {rows.length === 0 && !loading && <div className="p-10 text-center text-[#4b5d7a] text-sm">Aucun courriel journalisé.</div>}
                <ul>
                    {rows.map((r) => (
                        <li key={r.message_id} data-testid={`email-row-${r.message_id}`}
                            onClick={() => openDetail(r.message_id)}
                            className="border-b border-[#dde5f0] last:border-b-0 grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-3 px-4 py-3 items-center hover:bg-[#f3f6fb] cursor-pointer text-sm">
                            <div className="md:col-span-2 text-xs font-mono text-[#4b5d7a]">{fmt(r.created_at)}</div>
                            <div className="md:col-span-3 truncate">{(r.to || []).join(", ")}</div>
                            <div className="md:col-span-3 truncate flex items-center gap-1">
                                {(r.attachments || []).length > 0 && <Paperclip className="w-3 h-3 text-[#4b5d7a] shrink-0" />}
                                {r.subject}
                            </div>
                            <div className="md:col-span-1 text-xs">{TYPE_LABELS[r.email_type] || r.email_type}</div>
                            <div className="md:col-span-1 text-xs">{TRIGGER_LABELS[r.trigger] || r.trigger}</div>
                            <div className="md:col-span-2">
                                <span className={`tech-stamp px-2 py-1 border text-[10px] ${STATUS_BADGE[r.status] || "border-[#dde5f0]"}`}>
                                    {r.status}
                                </span>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {detail && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
                    <div className="bg-white max-w-3xl w-full my-8 border border-[#dde5f0]" onClick={(e) => e.stopPropagation()} data-testid="email-detail">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-[#dde5f0] bg-[#f3f6fb]">
                            <h3 className="font-display font-bold uppercase tracking-tight">{detail.subject}</h3>
                            <button onClick={() => setDetail(null)} data-testid="email-detail-close"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-3 text-sm">
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="tech-stamp text-[#4b5d7a]">Destinataire</span><div>{(detail.to || []).join(", ")}</div></div>
                                <div><span className="tech-stamp text-[#4b5d7a]">Envoyé le</span><div>{fmt(detail.created_at)}</div></div>
                                <div><span className="tech-stamp text-[#4b5d7a]">Type</span><div>{TYPE_LABELS[detail.email_type] || detail.email_type}</div></div>
                                <div><span className="tech-stamp text-[#4b5d7a]">Déclencheur</span><div>{TRIGGER_LABELS[detail.trigger] || detail.trigger}{detail.actor ? ` (${detail.actor})` : ""}</div></div>
                                {detail.linked_document && <div><span className="tech-stamp text-[#4b5d7a]">Document lié</span><div>{detail.linked_document.label || detail.linked_document.kind}</div></div>}
                                {detail.bcc && <div><span className="tech-stamp text-[#4b5d7a]">BCC</span><div>{detail.bcc}</div></div>}
                            </div>

                            {(detail.attachments || []).length > 0 && (
                                <div>
                                    <span className="tech-stamp text-[#4b5d7a] block mb-1">Pièces jointes</span>
                                    {detail.attachments.map((a) => (
                                        <button key={a.sha256} onClick={() => viewAttachment(detail.message_id, a.sha256)}
                                            data-testid={`attachment-${a.sha256}`}
                                            className="btn-secondary !py-1.5 !px-3 !text-xs flex items-center gap-1 mb-1">
                                            <Eye className="w-3 h-3" /> {a.filename} ({Math.round((a.size || 0) / 1024)} Ko)
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div>
                                <span className="tech-stamp text-[#4b5d7a] block mb-1">Corps exact envoyé</span>
                                <iframe title="corps" srcDoc={detail.body_html} data-testid="email-body"
                                    className="w-full h-72 border border-[#dde5f0] bg-white" sandbox="" />
                            </div>

                            {(detail.status_history || []).length > 0 && (
                                <div>
                                    <span className="tech-stamp text-[#4b5d7a] block mb-1">Historique de statut</span>
                                    {detail.status_history.map((h, i) => (
                                        <div key={i} className="text-xs flex items-center gap-2">
                                            <span className={`tech-stamp px-2 py-0.5 border text-[10px] ${STATUS_BADGE[h.phase] || "border-[#dde5f0]"}`}>{h.phase}</span>
                                            <span className="text-[#4b5d7a] font-mono">{fmt(h.at)}</span>
                                            {h.resend_id && <span className="text-[#4b5d7a]">Resend: {h.resend_id}</span>}
                                            {h.error && <span className="text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{h.error}</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
